import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../Firebase';
import './SpeedTest.css';

const SpeedTest = () => {
    const navigate = useNavigate();
    const [popularCampaigns, setPopularCampaigns] = useState([]);
    const [specialCampaigns, setSpecialCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const fetchCampaigns = async () => {
            try {
                setLoading(true);

                const providersQuery = query(
                    collection(db, 'providers'),
                    where('isActive', '==', true)
                );

                const providersSnapshot = await getDocs(providersQuery);
                const providersData = providersSnapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data()
                }));

                const allCampaigns = [];

                for (const provider of providersData) {
                    const campaignsQuery = query(
                        collection(db, `providers/${provider.id}/campaigns`),
                        where('isActive', '==', true)
                    );

                    const campaignsSnapshot = await getDocs(campaignsQuery);

                    campaignsSnapshot.forEach((doc) => {
                        allCampaigns.push({
                            id: doc.id,
                            providerId: provider.id,
                            providerName: provider.name,
                            providerLogo: provider.logo,
                            ...doc.data()
                        });
                    });
                }

                setPopularCampaigns(
                    allCampaigns
                        .filter((campaign) => {
                            const speed = Number(campaign.speed || 0);
                            return speed >= 100 && speed <= 500;
                        })
                        .sort((a, b) => Number(a.speed || 0) - Number(b.speed || 0))
                        .slice(0, windowWidth > 768 ? 2 : 1)
                );

                setSpecialCampaigns(
                    allCampaigns
                        .filter((campaign) => {
                            const speed = Number(campaign.speed || 0);
                            return speed > 500 || campaign.tags?.includes('special');
                        })
                        .sort((a, b) => Number(b.speed || 0) - Number(a.speed || 0))
                        .slice(0, windowWidth > 768 ? 2 : 1)
                );
            } catch (error) {
                console.error('Error fetching campaigns:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchCampaigns();
    }, [windowWidth]);

    const renderPriceInfo = (campaign) => {
        if (campaign.pricingModel === 'fixed') {
            return (
                <div className="price-section">
                    {campaign.oldPrice && <span className="old-price">{campaign.oldPrice} TL</span>}
                    <span className="new-price">{campaign.fixedMonthlyPrice} TL</span>
                    <span className="price-description">/ay</span>
                </div>
            );
        }

        const safePriceTiers = Array.isArray(campaign.priceTiers) ? campaign.priceTiers : [];
        const totalDuration = Number(campaign.totalDuration || 0);

        const totalCost = safePriceTiers.reduce((sum, tier) => {
            const monthlyPrice = Number(tier?.monthlyPrice || 0);
            const duration = Number(tier?.duration || 0);
            return sum + monthlyPrice * duration;
        }, 0);

        const averagePrice = totalDuration > 0 ? Math.round(totalCost / totalDuration) : 0;

        return (
            <div className="price-section">
                <span className="new-price">{averagePrice} TL</span>
                <span className="price-description">/ay (ortalama)</span>
            </div>
        );
    };

    const renderCampaignCard = (campaign) => (
        <div
            key={`${campaign.providerId}-${campaign.id}`}
            className="campaign-card"
            onClick={() => navigate(`/kampanyalar/${campaign.providerId}/${campaign.id}`)}
        >
            <div className="card-header">
                <img
                    src={campaign.providerLogo}
                    alt={campaign.providerName}
                    className="provider-logo"
                    loading="lazy"
                />
                <div className="provider-info">
                    <p className="provider-name">{campaign.providerName}</p>
                    <p className="campaign-speed">{campaign.speed} Mbps</p>
                </div>
            </div>

            <div className="card-body">
                <h3 className="campaign-title">{campaign.title}</h3>
                {renderPriceInfo(campaign)}
            </div>

            <div className="card-footer">
                <button type="button" className="view-details-button">
                    Detayları Gör
                    <svg width="16" height="16" viewBox="0 0 16 16">
                        <path
                            d="M3.33337 8H12.6667M12.6667 8L8.00004 3.33334M12.6667 8L8.00004 12.6667"
                            stroke="currentColor"
                            strokeWidth="1.5"
                        />
                    </svg>
                </button>
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="speed-test-container">
                <h2>İnternet Hız Testi</h2>
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Kampanyalar yükleniyor...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="speed-test-container">
            <div className="speed-test-header">
                <h2>İnternet Hız Testi</h2>
                <p className="subtitle">
                    Bağlantınızın gerçek hızını ölçün ve size uygun fiber internet paketini seçin
                </p>
            </div>

            <div className="speed-test-content">
                {windowWidth > 992 && (
                    <div className="campaign-column">
                        <h3 className="column-title">Popüler Paketler</h3>
                        {popularCampaigns.map(renderCampaignCard)}
                    </div>
                )}

                <div className="speed-test-wrapper">
                    <div className="iframe-container">
                        <iframe
                            src="https://fast.com"
                            title="İnternet Hız Testi"
                            className="speed-test-iframe"
                            allow="fullscreen"
                            loading="lazy"
                        />
                    </div>

                    <div className="test-tips">
                        <h4>Hız Testi İpuçları:</h4>
                        <ul>
                            <li>Test sırasında diğer cihazların internet kullanımını durdurun</li>
                            <li>Modeminize kablo ile bağlanarak daha doğru sonuç alabilirsiniz</li>
                            {windowWidth > 480 && (
                                <li>Testi farklı saatlerde tekrarlayarak karşılaştırma yapın</li>
                            )}
                        </ul>
                    </div>
                </div>

                {windowWidth > 992 ? (
                    <div className="campaign-column">
                        <h3 className="column-title">Özel Paketler</h3>
                        {specialCampaigns.map(renderCampaignCard)}
                    </div>
                ) : (
                    <div className="mobile-campaigns">
                        <div className="campaign-column">
                            <h3 className="column-title">Popüler Paketler</h3>
                            {popularCampaigns.map(renderCampaignCard)}
                        </div>
                        <div className="campaign-column">
                            <h3 className="column-title">Özel Paketler</h3>
                            {specialCampaigns.map(renderCampaignCard)}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SpeedTest;